import { generateUUID } from '../Const';

export const FAN_POLL_TYPES = [
    { value: 'ARTISTS', label: 'Artists' },
    { value: 'GENRE_DECADE', label: 'Genre / decade' },
    { value: 'CUSTOM', label: 'Custom' },
];

const emptyOption = (displayOrder) => ({
    _key: generateUUID(),
    label: '',
    displayOrder,
});

export const createEmptyFanPoll = () => ({
    id: null,
    label: '',
    question: '',
    subject: '',
    description: '',
    seoTitle: '',
    pollType: 'ARTISTS',
    imageMetadata: null,
    options: Array.from({ length: 5 }, (_, index) => emptyOption(index)),
    allowSuggestion: true,
    startDate: null,
    endDate: null,
    mainTag: null,
    tagList: [],
});

export const createFanPollForSameTopic = (draft) => ({
    ...createEmptyFanPoll(),
    pollType: draft.pollType,
    mainTag: draft.mainTag,
    tagList: [...(draft.tagList ?? [])],
});

const normalizePollType = (pollType) => (
    ['GENRES', 'DECADES'].includes(pollType) ? 'GENRE_DECADE'
        : ['ARTIST_ALBUMS', 'SONGS'].includes(pollType) ? 'ARTISTS'
            : pollType
);

export const mapFanPollResponseToDraft = (poll) => ({
    id: poll?.id ?? null,
    label: poll?.label ?? '',
    question: poll?.question ?? '',
    subject: poll?.subject ?? '',
    description: poll?.description ?? '',
    seoTitle: poll?.seoTitle ?? '',
    pollType: normalizePollType(poll?.pollType) ?? 'ARTISTS',
    imageMetadata: poll?.imageMetadata ?? null,
    options: [...(poll?.options ?? [])]
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map((option, index) => ({
            id: option.id,
            _key: option.id ?? generateUUID(),
            label: option.label ?? '',
            displayOrder: index,
        })),
    allowSuggestion: Boolean(poll?.allowSuggestion),
    startDate: poll?.startDate ?? null,
    endDate: poll?.endDate ?? null,
    mainTag: poll?.mainTag ?? null,
    tagList: poll?.tagList ?? [],
});

export const toFanPollRequest = (draft) => ({
    id: draft.id ?? null,
    label: draft.label?.trim() ?? '',
    question: draft.question?.trim() ?? '',
    subject: draft.subject?.trim() ?? '',
    description: draft.description?.trim() ?? '',
    seoTitle: draft.seoTitle?.trim() ?? '',
    pollType: draft.pollType,
    imageMetadata: draft.imageMetadata,
    options: (draft.options ?? []).map((option, index) => ({
        ...(option.id ? { id: option.id } : {}),
        label: option.label?.trim() ?? '',
        displayOrder: index,
    })),
    allowSuggestion: Boolean(draft.allowSuggestion),
    startDate: draft.startDate ?? null,
    endDate: draft.endDate ?? null,
    mainTag: draft.mainTag,
    tagList: draft.tagList ?? [],
});

export const normalizedDraftJson = (draft) => JSON.stringify(toFanPollRequest(draft));

export const calculateFanPollState = (draft, published) => {
    if (!published) return 'DRAFT';
    const now = Date.now();
    const start = draft?.startDate ? Date.parse(draft.startDate) : null;
    const end = draft?.endDate ? Date.parse(draft.endDate) : null;
    if (start != null && start > now) return 'SCHEDULED';
    if (end != null && end <= now) return 'CLOSED';
    return 'ACTIVE';
};

export const isSameTag = (a, b) => {
    if (!a || !b) return false;
    const aWikidata = a?.idMapping?.WIKIDATA;
    const bWikidata = b?.idMapping?.WIKIDATA;
    if (aWikidata && bWikidata) return aWikidata === bWikidata;
    return (a.label ?? '').trim().toLowerCase() === (b.label ?? '').trim().toLowerCase();
};

export const ensureMainTagInTags = (mainTag, tags) => {
    if (!mainTag) return tags ?? [];
    const current = tags ?? [];
    return current.some(tag => isSameTag(tag, mainTag)) ? current : [...current, mainTag];
};

export const mergeFanPollTags = (mainTag, currentTags, generatedTags) => {
    const merged = [];
    for (const tag of [...(generatedTags ?? []), ...(currentTags ?? [])]) {
        if (tag && !merged.some(existing => isSameTag(existing, tag))) merged.push(tag);
    }
    return ensureMainTagInTags(mainTag, merged);
};
export const newFanPollOption = (displayOrder) => emptyOption(displayOrder);
